import useGetProjectFolders, {
  TFolder,
} from '@/api/queries/project/useGetProjectFolders';
import { buildFolderToPath } from '@/ui/pages/projectPage/components/FilesPanel/utils';
import { unflattenFoldersForTreeView } from '@/utils/files';
import {
  Button,
  Card,
  EmptyState,
  TreeView,
  TreeViewItemWithId,
} from '@payaca/ambrosia';
import { Droppable } from '@payaca/dnd';

import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';

export const TREE_NODE_DROP_TYPE = 'tree-node';

export type TProps = {
  projectId: string;
  expandedTreeNodeIds: Set<string>;
  workingFolder?: TFolder;
  onOpenCreateFolderModal: () => void;
  onExpandTreeNode: (treeItemId: string) => void;
  onCollapseTreeNodes: (treeItemIds: Set<string>) => void;
};

const FoldersTreeView: FC<TProps> = (props) => {
  const {
    projectId,
    expandedTreeNodeIds,
    workingFolder,
    onOpenCreateFolderModal,
    onExpandTreeNode,
    onCollapseTreeNodes,
  } = props;

  const { url } = useRouteMatch();

  const {
    projectFolders,
    nonRootProjectFolders,
    isLoading: isLoadingProjectFolders,
  } = useGetProjectFolders(projectId);

  const unflattenedProjectFolders = useMemo(() => {
    return unflattenFoldersForTreeView(projectFolders || []);
  }, [projectFolders]);

  return (
    <>
      <Card className="md:min-w-52 md:max-w-96" sizeVariant="sm">
        <Card.Body className="flex flex-col h-full">
          {!isLoadingProjectFolders && (
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={onOpenCreateFolderModal}
                className="w-full mb-3"
              >
                Create folder
              </Button>
              {nonRootProjectFolders?.length === 0 && (
                <EmptyState
                  iconName="folder-search.3"
                  className="max-w-28 text-center my-6 mx-auto"
                  text="No folders found for this project"
                />
              )}
            </div>
          )}

          <TreeView
            items={unflattenedProjectFolders}
            expandedIds={expandedTreeNodeIds}
            onExpandItem={(treeItem, expanded) => {
              if (expanded) {
                onExpandTreeNode(treeItem.id);
              } else {
                // If collapsing, collapse all children too
                onCollapseTreeNodes(getItemIdAndChildrenIds(treeItem));
              }
            }}
            labelRenderer={(item, expanded) => {
              const hasChildren =
                Array.isArray(item.children) && item.children.length > 0;

              const folder = projectFolders?.find(
                (folder) => folder.id === item.id
              );

              return (
                <Droppable
                  id={`tree-node::${item.id}`}
                  data={{
                    type: TREE_NODE_DROP_TYPE,
                    folderId: item.id,
                    hasChildren,
                  }}
                >
                  <Link
                    className={clstx(
                      // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values
                      'flex cursor-pointer hover:no-underline text-[var(--tw-prose-root)] font-normal min-w-32 hover:bg-gray-100 items-center rounded-lg gap-2 data-[over]:ring-2 data-[over]:ring-blue-500 bg-transparent py-1 px-2 w-full',
                      item.id === workingFolder?.id && 'bg-gray-100'
                    )}
                    to={buildFolderToPath(url, folder)}
                    title={item.label}
                  >
                    <UntitledIcon
                      name={expanded ? 'folder-open' : 'folder.3'}
                      className="w-4 h-4 shrink-0"
                    />
                    <span className="truncate">{item.label}</span>
                  </Link>
                </Droppable>
              );
            }}
          />
        </Card.Body>
      </Card>
    </>
  );
};

const getItemIdAndChildrenIds = (
  item: TreeViewItemWithId | undefined
): Set<string> => {
  const toCollapse = new Set<string>();

  if (!item) return toCollapse;

  toCollapse.add(item.id);

  if (item.children && item.children.length > 0) {
    item.children.forEach((child) => {
      toCollapse.union(getItemIdAndChildrenIds(child));
    });
  }

  return toCollapse;
};

export default FoldersTreeView;
